import {
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import {FieldValues, useForm, SubmitHandler} from 'react-hook-form';
import {useState, useEffect} from 'react';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import AurIconButton from '../../../common/aur-button/AurIconButton';
import AurButton from '../../../common/aur-button/AurButton';
import {customIndexAnalyzerFormValidationSchema} from '../../../pages/user-management/validation-schemas';
import {yupResolver} from '@hookform/resolvers/yup';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {MAX_CHAR_LIMIT} from '../../../../constants/constants';
import {IAnalyzerConfig} from '../../model';

interface IAdjustAnalyzers {
  mode: string;
  selectedAnalyzer: IAnalyzerConfig;
  handleDuplicateAnalyser: () => void;
  handleAddAnalyzer: (data: FieldValues) => void;
  handleCancel: () => void;
}
interface FormValues {
  customName: string;
  stopWords: (string | undefined)[] | null;
  caseConversionFlag: boolean | null;
  whitespaceCharList: string | null;
}

export const AdjustAnalyzersContent = (props: IAdjustAnalyzers) => {
  const {mode, handleAddAnalyzer, handleCancel, selectedAnalyzer} = props;
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: {errors, isValid},
  } = useForm<FormValues>({
    mode: 'all',

    resolver: yupResolver(customIndexAnalyzerFormValidationSchema()),
  });

  const [stopWordsValue, setStopWordsValue] = useState<string>('');
  const [stopWordsList, setStopWordsList] = useState<string[]>([]);
  const [customNameError, setCustomNameError] = useState<string>('');

  const indexAnalyzers = useSelector(
    (state: RootState) => state.indexes.indexAnalyzers
  );

  const addStopWords = () => {
    const splitWords = stopWordsValue
      .split(' ')
      .map((word) => word.trim())
      .filter((word) => word.length > 0);
    const updatedStopWordsList = [...stopWordsList, ...splitWords];
    setStopWordsList(updatedStopWordsList);
    setStopWordsValue('');
    setValue('stopWords', updatedStopWordsList);
  };

  const handleDelete = (chipToDelete: string) => {
    setStopWordsList((prevLists) =>
      prevLists.filter((chip) => chip !== chipToDelete)
    );
    const removeData = stopWordsList.filter((chip) => chip !== chipToDelete);
    setValue('stopWords', removeData);
  };

  const handleRemoveAll = () => {
    setStopWordsList([]);
    setValue('stopWords', []);
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    handleAddAnalyzer(data);
  };

  useEffect(() => {
    if (selectedAnalyzer) {
      reset({
        customName: selectedAnalyzer.name,
        stopWords: selectedAnalyzer.stopWordsList,
        caseConversionFlag: selectedAnalyzer.caseConversionFlag,
        whitespaceCharList: selectedAnalyzer.whitespaceCharList,
      });
      if (selectedAnalyzer.stopWordsList?.length != 0) {
        setStopWordsList(selectedAnalyzer.stopWordsList);
      }
    }
  }, [selectedAnalyzer]);

  const isSaveDisabled = !isValid || customNameError != '';

  const validateAnalyzer = async (analyzer: string) => {
    const fiterAnalyzer = indexAnalyzers.find(
      (data: IAnalyzerConfig) => data.name === analyzer
    );
    if (fiterAnalyzer) {
      setCustomNameError(t('indexes.customIndex.step3.customNameError'));
    } else {
      setCustomNameError('');
    }
    await trigger('customName');
  };

  const resolvedHelperText =
    errors.customName?.message ||
    (customNameError !== ''
      ? customNameError
      : t('indexes.customIndex.helperText'));

  return (
    <div className="adjust-analyser-content">
      <Typography
        className="typo-text subhead1_size custom-name-text"
        tabIndex={0}
      >
        {mode === 'Edit'
          ? `${selectedAnalyzer.label} Settings`
          : t('indexes.customIndex.step3.createNewCustomAnalyzer')}
      </Typography>
      <Typography className="typo-text body3_size text analyzer-header-description">
        {selectedAnalyzer.description}
      </Typography>

      {mode === 'New' && (
        <div className="custom-analyzer-name">
          <Typography className="typo-text subhead4_size  custom-name-text">
            {t('indexes.customIndex.step3.customName')}
          </Typography>
          <TextField
            {...control.register('customName', {required: mode === 'New'})}
            className="adjust-analyser-field"
            inputProps={{
              'data-testid': 'custom-name',
              maxLength: MAX_CHAR_LIMIT,
            }}
            InputProps={{
              disableUnderline: true,
              endAdornment:
                errors?.customName?.message || customNameError ? (
                  <InputAdornment position="end">
                    <AurIcon
                      icon={IconNames.Error}
                      color={IconColors.Critical}
                    />
                  </InputAdornment>
                ) : null,

              className:
                (errors?.customName?.message || customNameError) &&
                'inputError',
            }}
            variant="filled"
            fullWidth
            onChange={(e) => {
              setValue('customName', e.target.value);
              validateAnalyzer(e.target.value); // Trigger custom validation
            }}
            label={t('indexes.customIndex.step3.customPlaceHolder')}
            helperText={resolvedHelperText}
            FormHelperTextProps={{
              className:
                errors?.customName?.message || customNameError != ''
                  ? 'label-error'
                  : 'custom-name-helper',
            }}
            error={!!errors.customName || customNameError != ''}
          />
        </div>
      )}

      <Divider className="adjust-analyzer-divider" />

      <div className="custom-analyzer-name">
        <div className="edit-stop-title">
          <div className="title-icon">
            <Typography className="typo-text subhead4_size text custom-name-text">
              {mode === 'New'
                ? t('indexes.customIndex.step3.editStopWords')
                : t('indexes.customIndex.step3.stopWords')}
            </Typography>
            <AurTooltip
              arrowPosition="right"
              title={t(`indexes.customIndexToolTip.stopWordDescription`)}
              className="typo-text label3_size"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {offset: [0, -6]},
                    },
                  ],
                },
              }}
            >
              <Typography
                className="analyzer-info-icon"
                tabIndex={0}
                role="button"
              >
                <AurIcon
                  icon={IconNames.Error}
                  size="medium"
                  color={IconColors.Primary}
                />
              </Typography>
            </AurTooltip>
          </div>
          {mode === 'New' && (
            <Typography className="typo-text body3_size text description-color">
              {t('indexes.customIndex.step3.editStopDescription')}
            </Typography>
          )}
        </div>

        {mode === 'New' && (
          <TextField
            className="adjust-analyser-field"
            inputProps={{
              'data-testid': 'custom-name',
            }}
            InputProps={{
              disableUnderline: true,
              endAdornment: mode === 'New' && (
                <InputAdornment position="end">
                  <AurButton
                    type="continue"
                    label={t('common.ipAddress.add')}
                    title={t('common.ipAddress.add')}
                    size="Medium"
                    onClick={addStopWords}
                    dataTestId="closeBtn-test"
                    disabled={stopWordsValue === ''}
                  />
                </InputAdornment>
              ),
            }}
            variant="filled"
            fullWidth
            label={t('indexes.customIndex.step3.editStopPlaceholder')}
            FormHelperTextProps={{
              className: 'custom-name-helper',
            }}
            onChange={(e) => setStopWordsValue(e.target.value)}
            value={stopWordsValue}
          />
        )}

        <div
          className={`stopwords-action ${mode === 'New' && 'stopwords-action-custom'}`}
        >
          {stopWordsList?.length !== 0 &&
            stopWordsList.map((chip) => (
              <Chip
                key={chip}
                className={`chip-stopwords-selection ${mode === 'New' && 'custom-chip'}`}
                label={
                  <div className="chip-words-label">
                    <Typography className="typo-text label3_size chip-words">
                      {chip}
                    </Typography>
                    {mode === 'New' && (
                      <AurIconButton
                        onClick={() => handleDelete(chip)}
                        className="chip-words-close"
                        icon={<AurIcon icon={IconNames.Close} size="medium" />}
                        dataTestId="Scroll Left"
                      />
                    )}
                  </div>
                }
                variant="outlined"
                size="small"
                color="primary"
              />
            ))}
          {stopWordsList.length !== 0 && mode === 'New' && (
            <AurButton
              type="text"
              title={t('searchableDropdown.clearAll')}
              label={t('common.removeAll')}
              size="Medium"
              onClick={handleRemoveAll}
              dataTestId="cancel-button"
            />
          )}
        </div>
      </div>

      <Divider className="adjust-analyzer-divider" />

      {/* Alphabetical Field */}
      <div className="custom-analyzer-name">
        <div className="edit-stop-title">
          <div className="title-icon">
            <Typography className="typo-text subhead4_size text custom-name-text">
              {mode === 'New'
                ? t('indexes.customIndex.step3.editAlphabeticalLabel')
                : t('indexes.customIndex.step3.alphabeticalLabel')}
            </Typography>

            <AurTooltip
              title={t(
                `indexes.customIndexToolTip.alphabeticalCharactersDescription`
              )}
              className="typo-text label3_size"
              arrowPosition="right"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -3],
                      },
                    },
                  ],
                },
              }}
            >
              <Typography
                className="analyzer-info-icon"
                tabIndex={0}
                role="button"
                style={{display: 'inline-block'}}
              >
                <AurIcon
                  icon={IconNames.Error}
                  size="medium"
                  color={IconColors.Primary}
                />
              </Typography>
            </AurTooltip>
          </div>
          <Typography className="typo-text body3_size text description-color">
            {mode === 'New'
              ? t('indexes.customIndex.step3.editAlphabeticalDescription')
              : t('indexes.customIndex.step3.alphabeticalDescription')}
          </Typography>
        </div>

        <div className="analyzer-radio-container">
          <FormControlLabel
            tabIndex={0}
            control={
              <Radio
                checked={watch('caseConversionFlag') === true}
                onChange={() => setValue('caseConversionFlag', true)}
                disabled={mode !== 'New'}
                // aria-checked={watch('caseConversionFlag') === true}
              />
            }
            label={
              <Typography
                className={`typo-text body3_size custom-name-text ${mode !== 'New' && watch('caseConversionFlag') === false && 'disable-radio-label'}`}
              >
                {t('indexes.customIndex.step3.lowerCase')}
              </Typography>
            }
          />
          <FormControlLabel
            tabIndex={0}
            control={
              <Radio
                checked={watch('caseConversionFlag') === false}
                onChange={() => setValue('caseConversionFlag', false)}
                disabled={mode !== 'New'}
              />
            }
            label={
              <Typography
                className={`typo-text body3_size  custom-name-text ${mode !== 'New' && watch('caseConversionFlag') === true && 'disable-radio-label'}`}
              >
                {t('indexes.customIndex.step3.upperCase')}
              </Typography>
            }
          />
        </div>
      </div>

      <Divider className="adjust-analyzer-divider" />

      {/* Whitespace Field */}
      <div className="custom-analyzer-name whitespace-container">
        <div className="edit-stop-title">
          <div className="title-icon">
            <Typography
              className="typo-text subhead4_size text custom-name-text"
              tabIndex={0}
            >
              {mode === 'New'
                ? t('indexes.customIndex.step3.editWhitespaceLabel')
                : t('indexes.customIndex.step3.whitespaceLabel')}
            </Typography>
            <AurTooltip
              tabIndex={0}
              title={t(
                `indexes.customIndexToolTip.whitespaceCharactersDescription`
              )}
              className="typo-text label3_size"
              arrowPosition="right"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {offset: [0, -6]},
                    },
                  ],
                },
              }}
            >
              <Typography
                className="analyzer-info-icon"
                tabIndex={0}
                role="button"
              >
                <AurIcon
                  icon={IconNames.Error}
                  size="medium"
                  color={IconColors.Primary}
                />
              </Typography>
            </AurTooltip>
          </div>
          <Typography className="typo-text body3_size text description-color">
            {t('indexes.customIndex.step3.editWhitespaceDescription')}
          </Typography>
        </div>
        {mode === 'New' || selectedAnalyzer.whitespaceCharList === '' ? (
          <TextField
            {...control.register('whitespaceCharList', {required: false})}
            className="adjust-analyser-field"
            inputProps={{
              'data-testid': 'whitespace-char-input',
            }}
            InputProps={{
              disableUnderline: true,
            }}
            variant="filled"
            fullWidth
            label={
              mode === 'Edit' && t('indexes.customIndex.step3.noneSelected')
            }
            helperText={
              mode === 'New' && t('indexes.customIndex.step3.languageTBD')
            }
            FormHelperTextProps={{
              className: 'whitespace-helper',
            }}
            error={!!errors.whitespaceCharList}
            disabled={mode != 'New'} // Disable in "Edit" mode
          />
        ) : (
          <Typography className="typo-text body3_size custom-name-text">
            {selectedAnalyzer.whitespaceCharList}
          </Typography>
        )}
      </div>
      {mode != 'New' && <Divider className="adjust-analyzer-divider" />}
      {mode === 'New' && (
        <div className="analyzer-action-container">
          <AurButton
            type="exit"
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            dataTestId={'closeBtn-test'}
            classNames="index-cancel-btn"
            onClick={handleCancel}
          />
          <AurButton
            type="continue"
            label={t('indexes.customIndex.step3.saveAddAnalyzer')}
            title={t('indexes.customIndex.step3.saveAddAnalyzer')}
            size="Medium"
            onClick={handleSubmit(onSubmit)}
            dataTestId={'closeBtn-test'}
            disabled={isSaveDisabled}
          />
        </div>
      )}
    </div>
  );
};
